import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RefreshCw, Search } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { RentalStatus, RentalType } from "@/shared/types/enums";
import { productService } from "@/features/products/services/product-api.service";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { ROUTES } from "@/constants/routes";
import type {
  CreateRentalDTO,
  PickupDTO,
  ReturnDTO,
} from "@/features/rentals/admin/types";
import {
  useRentalList,
  RentalsTable,
  RentalDetailDialog,
  RentalDeleteDialog,
  PickupModal,
  ReturnModal,
  RentalWizard,
  useRentalWizardLists,
  useRentalWizardSearch,
  rentalAdminService,
} from "@/features/rentals/admin";

export function AlugueisPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep link `?rentalId=X` (vindo do modal de cliente, por exemplo): lido
  // uma única vez na montagem — depois o estado dos filtros é soberano.
  const [deepLinkRentalId] = useState<number | undefined>(() => {
    const raw = searchParams.get("rentalId");
    if (!raw || !/^\d+$/.test(raw)) return undefined;
    const parsed = Number(raw);
    return parsed >= 1 && parsed <= 2147483647 ? parsed : undefined;
  });

  const [pageSize, setPageSize] = useState(10);
  const {
    data: rentals,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    setFilters,
    goToPage,
    reload,
    updateLocal,
    removeLocal,
  } = useRentalList(pageSize, { rentalId: deepLinkRentalId });

  const [pendingRentalId, setPendingRentalId] = useState(
    deepLinkRentalId ? String(deepLinkRentalId) : "",
  );
  const [pendingContractNumber, setPendingContractNumber] = useState("");
  const [pendingClientName, setPendingClientName] = useState("");
  const [pendingType, setPendingType] = useState<RentalType | "">("");
  const [pendingStatus, setPendingStatus] = useState<RentalStatus | "">("");
  const [pendingStartDate, setPendingStartDate] = useState("");
  const [pendingEndDate, setPendingEndDate] = useState("");
  const [pendingCreatedAt, setPendingCreatedAt] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // O dialog de edição tem selects próprios, então precisa das listas; a
  // página de criação (/alugueis/novo-admin) usa só as buscas.
  const { sellers, gownProducts, tags } = useRentalWizardLists();
  const wizardSearch = useRentalWizardSearch();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingRental, setEditingRental] = useState<RentalResponseDTO | null>(
    null,
  );
  // Ordinal de abertura: entra na `key` do wizard para que reabrir o MESMO
  // aluguel também remonte os passos (ver o comentário na montagem).
  const [wizardOpenCount, setWizardOpenCount] = useState(0);

  const [detailRental, setDetailRental] = useState<RentalResponseDTO | null>(
    null,
  );
  const [pickupRental, setPickupRental] = useState<RentalResponseDTO | null>(
    null,
  );
  const [returnRental, setReturnRental] = useState<RentalResponseDTO | null>(
    null,
  );
  const [returnWarning, setReturnWarning] = useState<string | null>(null);
  const [deleteRental, setDeleteRental] = useState<RentalResponseDTO | null>(
    null,
  );
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const tagsMap = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  const filteredRentals = useMemo(() => {
    if (!searchTerm) return rentals;
    const s = searchTerm.toLowerCase();
    return rentals.filter(
      (r) =>
        r.contractNumber?.toLowerCase().includes(s) ||
        r.clientName?.toLowerCase().includes(s) ||
        r.sellerName?.toLowerCase().includes(s),
    );
  }, [rentals, searchTerm]);

  const getProductById = useCallback(
    async (
      id: number,
      signal?: AbortSignal,
    ): Promise<ProductResponseDTO | null> => {
      try {
        return await productService.getById(id, { signal });
      } catch {
        return null;
      }
    },
    [],
  );

  const handleSearch = useCallback(() => {
    const idTerm = pendingRentalId.trim();
    let rentalId: number | undefined;
    if (idTerm) {
      // Valida a string crua (não Number()): mata notação científica tipo
      // "1e5" e o backend binda Integer — acima de 2^31-1 quebra a conversão.
      const numeric = /^\d+$/.test(idTerm) ? Number(idTerm) : NaN;
      if (!Number.isInteger(numeric) || numeric <= 0 || numeric > 2147483647) {
        toast.error(
          "ID do aluguel inválido. Informe um número inteiro positivo.",
        );
        return;
      }
      rentalId = numeric;
    }
    setFilters({
      rentalId,
      contractNumber: pendingContractNumber.trim() || undefined,
      clientName: pendingClientName.trim() || undefined,
      rentalType: (pendingType as RentalType) || undefined,
      status: (pendingStatus as RentalStatus) || undefined,
      startDate: pendingStartDate || undefined,
      endDate: pendingEndDate || undefined,
      createdAt: pendingCreatedAt || undefined,
    });
  }, [
    pendingRentalId,
    pendingContractNumber,
    pendingClientName,
    pendingType,
    pendingStatus,
    pendingStartDate,
    pendingEndDate,
    pendingCreatedAt,
    setFilters,
  ]);

  const handleClearFilters = useCallback(() => {
    setPendingRentalId("");
    setPendingContractNumber("");
    setPendingClientName("");
    setPendingType("");
    setPendingStatus("");
    setPendingStartDate("");
    setPendingEndDate("");
    setPendingCreatedAt("");
    setFilters({});
  }, [setFilters]);

  // Deep link: abre o detalhe do aluguel quando ele chega na lista.
  // Uma única vez por montagem (ref) — fechar o modal não o reabre.
  // Adiado para o próximo tick: evita setState síncrono no efeito.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!deepLinkRentalId || autoOpenedRef.current) return;
    const rental = rentals.find((item) => item.id === deepLinkRentalId);
    if (!rental) return;
    const timer = setTimeout(() => {
      autoOpenedRef.current = true;
      setDetailRental(rental);
    }, 0);
    return () => clearTimeout(timer);
  }, [deepLinkRentalId, rentals]);

  // Timeout da animação de fechamento do wizard — cancelado ao reabrir para o
  // conteúdo do dialog novo não ser resetado pelo fechamento anterior.
  const wizardCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (wizardCloseTimeout.current) clearTimeout(wizardCloseTimeout.current);
    },
    [],
  );

  const openEditWizard = useCallback((rental: RentalResponseDTO) => {
    if (wizardCloseTimeout.current) {
      clearTimeout(wizardCloseTimeout.current);
      wizardCloseTimeout.current = null;
    }
    setWizardOpen(true);
    setEditingRental(rental);
    setWizardOpenCount((count) => count + 1);
  }, []);

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    wizardCloseTimeout.current = setTimeout(() => {
      setEditingRental(null);
      wizardCloseTimeout.current = null;
    }, 300);
  }, []);

  const handleWizardSubmit = useCallback(
    async (dto: CreateRentalDTO) => {
      if (!editingRental) return;
      try {
        const updated = await rentalAdminService.update(editingRental.id, {
          ...dto,
          status: editingRental.status,
        });
        updateLocal(updated);
        toast.success("Aluguel atualizado com sucesso!");
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao salvar aluguel"));
        // Relançado de propósito: mantém o dialog aberto com os dados.
        throw error;
      }
    },
    [editingRental, updateLocal],
  );

  const handlePickupConfirm = useCallback(
    async (dto: PickupDTO) => {
      if (!pickupRental) return;
      setLoadingId(pickupRental.id);
      try {
        const updated = await rentalAdminService.pickup(pickupRental.id, dto);
        updateLocal(updated);
        toast.success("Retirada registrada com sucesso!");
        setPickupRental(null);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao registrar retirada"));
      } finally {
        setLoadingId(null);
      }
    },
    [pickupRental, updateLocal],
  );

  const handleReturnConfirm = useCallback(
    async (dto: ReturnDTO) => {
      if (!returnRental) return;
      setLoadingId(returnRental.id);
      setReturnWarning(null);
      try {
        const updated = await rentalAdminService.return(returnRental.id, dto);
        updateLocal(updated);
        toast.success("Devolução registrada com sucesso!");
        setReturnRental(null);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao registrar devolução"));
        try {
          const refreshed = await rentalAdminService.getById(returnRental.id);
          updateLocal(refreshed);
          setReturnRental(refreshed);
          if (refreshed.paidValue < refreshed.totalValue) {
            setReturnWarning(
              "O pagamento foi abatido do saldo, mas o aluguel não foi finalizado pois ainda há saldo devedor.",
            );
          }
        } catch {
          // se o reload falhar, ignora silenciosamente
        }
      } finally {
        setLoadingId(null);
      }
    },
    [returnRental, updateLocal],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteRental) return;
    setLoadingId(deleteRental.id);
    try {
      await rentalAdminService.cancel(deleteRental.id);
      removeLocal(deleteRental.id);
      toast.success("Aluguel cancelado com sucesso!");
      setDeleteRental(null);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao cancelar aluguel"));
    } finally {
      setLoadingId(null);
    }
  }, [deleteRental, removeLocal]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Aluguéis</h1>
          <p className="text-muted-foreground">
            {loading
              ? "Carregando..."
              : `${totalElements} aluguel(is) encontrado(s)`}
          </p>
        </div>
        <Can permission={ACTIONS.rentals.createAdmin}>
          <Button
            className="gap-2"
            onClick={() => navigate(ROUTES.RENTAL_CREATE_ADMIN)}
          >
            <Plus className="w-4 h-4" /> Novo aluguel
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <label
                className="text-sm font-medium"
                htmlFor="rental-admin-filter-contract"
              >
                Número do contrato
              </label>
              <Input
                id="rental-admin-filter-contract"
                placeholder="Ex.: 1047"
                value={pendingContractNumber}
                onChange={(e) => setPendingContractNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="space-y-1">
              <label
                className="text-sm font-medium"
                htmlFor="rental-admin-filter-id"
              >
                ID do aluguel
              </label>
              <Input
                id="rental-admin-filter-id"
                type="text"
                inputMode="numeric"
                placeholder="Ex.: 123"
                value={pendingRentalId}
                onChange={(e) => setPendingRentalId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="space-y-1">
              <label
                className="text-sm font-medium"
                htmlFor="rental-admin-filter-client"
              >
                Cliente
              </label>
              <Input
                id="rental-admin-filter-client"
                placeholder="Nome do cliente"
                value={pendingClientName}
                onChange={(e) => setPendingClientName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Tipo</label>
              <select
                value={pendingType}
                onChange={(e) =>
                  setPendingType(e.target.value as RentalType | "")
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Todos os tipos</option>
                <option value={RentalType.SUIT}>Terno</option>
                <option value={RentalType.GOWN}>Beca</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Status</label>
              <select
                value={pendingStatus}
                onChange={(e) =>
                  setPendingStatus(e.target.value as RentalStatus | "")
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Todos os status</option>
                <option value={RentalStatus.INCOMPLETE}>Incompleto</option>
                <option value={RentalStatus.COMPLETE}>Completo</option>
                <option value={RentalStatus.ON_STREET}>Na rua</option>
                <option value={RentalStatus.FINISHED}>Finalizado</option>
                <option value={RentalStatus.CANCELED}>Cancelado</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Período</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={pendingStartDate}
                  onChange={(e) => setPendingStartDate(e.target.value)}
                  title="Data inicial"
                />
                <Input
                  type="date"
                  value={pendingEndDate}
                  onChange={(e) => setPendingEndDate(e.target.value)}
                  title="Data final"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Criado em</label>
              <Input
                type="date"
                value={pendingCreatedAt}
                onChange={(e) => setPendingCreatedAt(e.target.value)}
                title="Data de criação"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} disabled={loading} className="gap-2">
              <Search className="w-4 h-4" /> Buscar
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardDescription>
              {loading
                ? "Carregando..."
                : `Mostrando ${filteredRentals.length} de ${totalElements}`}
            </CardDescription>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filtrar na tabela..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:max-w-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={reload}
                disabled={loading}
                aria-label="Atualizar lista"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                disabled={loading}
              >
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && rentals.length === 0 ? (
            <div className="flex justify-center py-12">
              <Spinner className="w-6 h-6" />
            </div>
          ) : error ? (
            <div className="text-center py-10 space-y-2">
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={reload}>
                Tentar novamente
              </Button>
            </div>
          ) : (
            <RentalsTable
              rentals={filteredRentals}
              loading={loading}
              page={page}
              totalPages={totalPages}
              tagsMap={tagsMap}
              loadingId={loadingId}
              onView={setDetailRental}
              onEdit={openEditWizard}
              onDelete={setDeleteRental}
              onPickup={setPickupRental}
              onReturn={setReturnRental}
              onPageChange={goToPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Montado só com um aluguel em mãos: `editingRental` sobrevive à
          animação de fechamento (300ms) e só então volta a null. */}
      {editingRental ? (
        <RentalWizard
          // Remonta a cada abertura: os forms de cada passo só leem
          // `defaultValues` no mount, então sem isto reabrir dentro da animação
          // de fechamento (300ms) exibiria o estado da abertura anterior — os
          // dados de outro aluguel, ou o que o usuário digitou e descartou no
          // mesmo. O ordinal só muda ao abrir, então o fechamento não é afetado.
          key={`${editingRental.id}-${wizardOpenCount}`}
          open={wizardOpen}
          editing={editingRental}
          sellers={sellers}
          tags={tags}
          gownProducts={gownProducts}
          searchContracts={wizardSearch.searchContracts}
          createContract={wizardSearch.createContract}
          searchClients={wizardSearch.searchClients}
          createClient={wizardSearch.createClient}
          searchStocks={wizardSearch.searchStocks}
          onSubmit={handleWizardSubmit}
          onClose={closeWizard}
        />
      ) : null}

      <RentalDetailDialog
        open={detailRental !== null}
        rental={detailRental}
        tagsMap={tagsMap}
        getProductById={getProductById}
        onClose={() => setDetailRental(null)}
      />

      <PickupModal
        open={pickupRental !== null}
        rental={pickupRental}
        loading={loadingId === pickupRental?.id}
        onConfirm={handlePickupConfirm}
        onClose={() => setPickupRental(null)}
      />

      <ReturnModal
        open={returnRental !== null}
        rental={returnRental}
        loading={loadingId === returnRental?.id}
        warning={returnWarning}
        onConfirm={handleReturnConfirm}
        onClose={() => {
          setReturnRental(null);
          setReturnWarning(null);
        }}
      />

      <RentalDeleteDialog
        open={deleteRental !== null}
        rental={deleteRental}
        loading={loadingId === deleteRental?.id}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteRental(null)}
      />
    </div>
  );
}
